<!-- src/apps/secret/components/receipt/StatusBadge.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import OIcon from '@/shared/components/icons/OIcon.vue';
  import { useSecretExpiration } from '@/shared/composables/useSecretExpiration';
  import { type Receipt } from '@/schemas/shapes/v3/receipt';
  import { getDisplayStatus, type DisplayStatus, getStatusText } from '@/utils/status';
  import { computed, watchEffect } from 'vue';

  interface Props {
    record: Receipt;
    expiresIn?: number;
  }

  const props = defineProps<Props>();
  const { t } = useI18n();

  const { expirationState } = useSecretExpiration(
    props.record.created,
    props.record.expiration_in_seconds ?? 0
  );

  const status = computed((): DisplayStatus => {
    // getDisplayStatus is the single display authority: it validates the raw
    // lifecycle state and tolerates legacy aliases ('viewed'/'received'), so we
    // pass the value straight through. Re-validating here against the strict v3
    // schema would throw on legacy receipts the display layer is built to
    // absorb (#3829).
    const stateValue = props.record.secret_state || props.record.state;
    const displayStatus = getDisplayStatus(stateValue, props.expiresIn);

    // A revealed or burned secret is gone; the TTL clock no longer describes
    // it. The receipt outlives the secret (receipt TTL is twice the secret's),
    // so without this a creator opening the receipt after the secret's TTL saw
    // "Expired" or "Expiring soon" in place of "Revealed" / "Burned".
    if (displayStatus === 'revealed' || displayStatus === 'burned') {
      return displayStatus;
    }

    // Map expiration states to display states
    if (expirationState.value === 'expired') {
      return 'expired';
    }
    if (expirationState.value === 'warning') {
      return 'expiring_soon';
    }

    if (displayStatus === 'new' && props.record.is_previewed) {
      return 'previewed';
    }
    return displayStatus;
  });

  // Status styling maps with enhanced design
  // Note: previewed/revealed are the new canonical states; viewed/received are deprecated aliases
  const statusClasses: Record<DisplayStatus, string> = {
    new: 'bg-gradient-to-r from-green-50 to-green-100 text-green-800 dark:from-green-900/60 dark:to-green-800/40 dark:text-green-300 border border-green-200 dark:border-green-800/50',
    unread: 'bg-gradient-to-r from-slate-50 to-slate-100 text-slate-800 dark:from-slate-900/60 dark:to-slate-800/40 dark:text-slate-300 border border-slate-200 dark:border-slate-800/50',
    viewed: 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 dark:from-blue-900/60 dark:to-blue-800/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50',
    previewed: 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-800 dark:from-blue-900/60 dark:to-blue-800/40 dark:text-blue-300 border border-blue-200 dark:border-blue-800/50',
    burned: 'bg-gradient-to-r from-yellow-50 to-yellow-100 text-yellow-800 dark:from-yellow-900/60 dark:to-yellow-800/40 dark:text-yellow-300 border border-yellow-200 dark:border-yellow-800/50',
    received: 'bg-gradient-to-r from-red-50 to-red-100 text-red-800 dark:from-red-900/60 dark:to-red-800/40 dark:text-red-300 border border-red-200 dark:border-red-800/50',
    revealed: 'bg-gradient-to-r from-red-50 to-red-100 text-red-800 dark:from-red-900/60 dark:to-red-800/40 dark:text-red-300 border border-red-200 dark:border-red-800/50',
    expiring_soon: 'bg-gradient-to-r from-orange-50 to-orange-100 text-orange-800 dark:from-orange-900/60 dark:to-orange-800/40 dark:text-orange-300 border border-orange-200 dark:border-orange-800/50',
    orphaned: 'bg-gradient-to-r from-purple-50 to-purple-100 text-purple-800 dark:from-purple-900/60 dark:to-purple-800/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50',
    expired: 'bg-gradient-to-r from-purple-50 to-purple-100 text-purple-800 dark:from-purple-900/60 dark:to-purple-800/40 dark:text-purple-300 border border-purple-200 dark:border-purple-800/50',
  };

  const statusIcon: Record<DisplayStatus, string> = {
    new: 'check-circle-outline',
    unread: 'mark-email-unread-outline',
    viewed: 'mark-email-unread-outline',
    previewed: 'mark-email-unread-outline',
    burned: 'local-fire-department-rounded',
    received: 'mark-email-read-outline',
    revealed: 'mark-email-read-outline',
    expiring_soon: 'timer-outline',
    orphaned: 'warning-outline',
    expired: 'timer-off-outline',
  };

  const displayStatus = computed(() => getStatusText(status.value, t));

  // Handle invalid states
  watchEffect(() => {
    if (!status.value) {
      console.error(
        `Invalid receipt state: ${props.record?.state} || ${props.record?.secret_state}`
      );
    }
  });
</script>

<template>
  <span
    class="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium shadow-sm transition-all duration-200"
    :class="[
      statusClasses[status],
      {
        'animate-pulse': status === 'expiring_soon',
        'hover:scale-105': status !== 'expired',
        'opacity-75': status === 'expired'
      }
    ]"
    :title="displayStatus.description"
    role="status">
    <OIcon
      collection="material-symbols"
      :name="statusIcon[status]"
      class="mr-1.5 size-4" />
    {{ displayStatus.text }}
  </span>
</template>

<style scoped>
/* Ensure high contrast in dark mode with enhanced styling */
.animate-pulse {
  animation: statusPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes statusPulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.7;
  }
}
</style>
